//TradingView.tsx
'use client';

import { useEffect, useState, useRef } from 'react';
import { TradingDashboard } from '@/components/component/dashboard/trading-dashboard';
import DashboardRecharts from './DashboardRecharts';

interface TradingViewProps {
  selectedSymbol?: string;
  onSymbolChanged?: (symbol: string) => void;
  isTvLibraryReady?: boolean;
  onLoadComplete?: () => void;
  forceSymbolUpdate?: boolean;
  chartType?: 'tradingview' | 'recharts'; // Only add this one new prop
}

export function TradingView({
  selectedSymbol = 'Rolex:16610',
  onSymbolChanged,
  isTvLibraryReady = false,
  onLoadComplete,
  forceSymbolUpdate = false,
  chartType = 'tradingview', // Default to TradingView
}: TradingViewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const initialLoadRef = useRef(true);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Function to notify chart container about size changes
  const notifyChartContainerResize = () => {
    if (!containerRef.current || !window.tvWidget) return;

    try {
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        console.log('Notifying chart container size changed:', {
          width: rect.width,
          height: rect.height,
        });

        // Use setChartSize which is available
        if (typeof window.tvWidget.setChartSize === 'function') {
          window.tvWidget.setChartSize(rect.width, rect.height);
        }

        // Ensure TradingView container styles are set correctly - FLEXIBLE SIZING
        const tradingViewContainer = containerRef.current.querySelector(
          '.tradingview-widget-container'
        ) as HTMLElement | null;
        if (tradingViewContainer) {
          tradingViewContainer.style.width = '100%'; // Take full width of parent
          tradingViewContainer.style.height = '100%'; // Take full height of parent
          tradingViewContainer.style.minHeight = '300px'; // Ensure a minimum height
          tradingViewContainer.style.position = 'relative';
          tradingViewContainer.style.overflow = 'hidden';
          tradingViewContainer.style.display = 'flex'; // Ensure it behaves as a flex container
        }

        // Find and fix the iframe - FLEXIBLE SIZING AND POSITIONING
        const tvIframe = containerRef.current.querySelector(
          'iframe.tradingview-widget-iframe'
        ) as HTMLIFrameElement | null;
        if (tvIframe) {
          tvIframe.style.width = '100%'; // Take full width of container
          tvIframe.style.height = '100%'; // Take full height of container
          tvIframe.style.minHeight = '300px';
          tvIframe.style.position = 'absolute'; // Absolutely position within container
          tvIframe.style.top = '0';
          tvIframe.style.left = '0';
          tvIframe.style.display = 'block'; // Ensure iframe is treated as block element
        }
      }
    } catch (err) {
      console.warn('Failed to notify chart container size:', err);
    }
  };

  // Update the chart when selectedSymbol changes - only adding chartType check
  useEffect(() => {
    if (
      !selectedSymbol ||
      initialLoadRef.current ||
      chartType !== 'tradingview'
    ) {
      initialLoadRef.current = false;
      return;
    }

    console.log(`TradingView: Forcing symbol update to ${selectedSymbol}`);

    // This approach will try multiple methods, from most direct to fallbacks
    const updateSymbol = () => {
      // Method 1: Direct window.tvWidget access (most reliable)
      if (typeof window !== 'undefined' && window.tvWidget) {
        try {
          const interval = window.tvWidget.symbolInterval?.()?.interval || '1D';

          console.log(
            `Method 1: Using window.tvWidget to set symbol ${selectedSymbol}`
          );

          // First explicitly clear the symbol - this forces a full reload
          window.tvWidget.setSymbol('', interval, () => {
            // Use a timeout to ensure the chart has time to process
            setTimeout(() => {
              window.tvWidget.setSymbol(selectedSymbol, interval, () => {
                console.log(
                  `Successfully updated chart to ${selectedSymbol} via window.tvWidget`
                );
                // Notify about container size after symbol change
                setTimeout(notifyChartContainerResize, 500);
              });
            }, 500);
          });
          return true;
        } catch (err) {
          console.error('Method 1 failed:', err);
        }
      }

      // Method 2: Use global helper if available
      if (
        typeof window !== 'undefined' &&
        typeof window.forceChartSymbolUpdate === 'function'
      ) {
        try {
          console.log(`Method 2: Using global helper for ${selectedSymbol}`);
          const result = window.forceChartSymbolUpdate(selectedSymbol);
          if (result) {
            console.log('Successfully updated via global helper');
            setTimeout(notifyChartContainerResize, 500);
            return true;
          }
        } catch (err) {
          console.error('Method 2 failed:', err);
        }
      }

      return false;
    };

    // Try update and log if failed
    if (!updateSymbol()) {
      console.error('All symbol update methods failed');
    }
  }, [selectedSymbol, forceSymbolUpdate, chartType]);

  // Store widget reference when available - only adding chartType check
  useEffect(() => {
    if (typeof window !== 'undefined' && chartType === 'tradingview') {
      // Function to check for widget existence
      const checkForWidget = () => {
        if (window.tvWidget) {
          widgetRef.current = window.tvWidget;
          console.log(
            'TradingView component: Stored widget reference from window'
          );

          // Force initial resize when widget is found
          setTimeout(notifyChartContainerResize, 200);

          return true;
        }
        return false;
      };

      // Check immediately
      if (!checkForWidget()) {
        // If not found, poll for it
        const intervalId = setInterval(() => {
          if (checkForWidget()) {
            clearInterval(intervalId);
          }
        }, 100);

        // Cleanup after 10 seconds
        const timeoutId = setTimeout(() => {
          clearInterval(intervalId);
        }, 10000);

        return () => {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
        };
      }
    }
  }, [chartType]);

  // Effect to listen for chartReady event from TradingDashboard
  useEffect(() => {
    const handleChartReady = (e: any) => {
      console.log('TradingView component: Chart ready event received');

      if (e.detail && e.detail.widget) {
        widgetRef.current = e.detail.widget;
        console.log(
          'TradingView component: Widget reference stored from event'
        );

        // Notify about container size when chart is ready
        setTimeout(notifyChartContainerResize, 200);

        // If we have a selected symbol, set it
        if (selectedSymbol && selectedSymbol !== 'Rolex:16610') {
          const interval = e.detail.widget.symbolInterval?.()?.interval || '1D';
          console.log(
            `TradingView component: Setting initial symbol to ${selectedSymbol}`
          );

          setTimeout(() => {
            e.detail.widget.setSymbol(selectedSymbol, interval);
            setTimeout(notifyChartContainerResize, 500);
          }, 1000); // Delay to ensure chart is fully loaded
        }
      }

      setIsLoading(false);
      if (onLoadComplete) onLoadComplete();
    };

    // Only add listener if we're showing TradingView
    if (chartType === 'tradingview') {
      window.addEventListener('tvChartReady', handleChartReady);
      return () => {
        window.removeEventListener('tvChartReady', handleChartReady);
      };
    }
  }, [selectedSymbol, onLoadComplete, chartType]);

  // Set up ResizeObserver to handle container resizing
  useEffect(() => {
    if (
      containerRef.current &&
      typeof ResizeObserver !== 'undefined' &&
      chartType === 'tradingview'
    ) {
      // Create a resize observer to handle container size changes
      resizeObserverRef.current = new ResizeObserver((entries) => {
        if (entries.length > 0) {
          // Get current dimensions
          const { width, height } = entries[0].contentRect;

          // Only trigger resize if dimensions are valid
          if (width > 0 && height > 0) {
            // Add debounce to avoid too many resize operations
            setTimeout(() => {
              notifyChartContainerResize();
            }, 100);
          }
        }
      });

      // Start observing the container
      resizeObserverRef.current.observe(containerRef.current);
    }

    // Cleanup function
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [chartType]);

  // Listen for window resize events to notify chart about size changes
  useEffect(() => {
    const handleWindowResize = () => {
      console.log('Window resize detected, notifying chart container');
      setTimeout(notifyChartContainerResize, 100);
    };

    if (chartType === 'tradingview') {
      window.addEventListener('resize', handleWindowResize);
      // Force an initial resize notification
      setTimeout(notifyChartContainerResize, 500);
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [chartType]);

  // Handle Split.js resize events
  useEffect(() => {
    const handleSplitResize = () => {
      console.log('Split resize event detected');
      setTimeout(notifyChartContainerResize, 100);
    };

    if (chartType === 'tradingview') {
      // Listen for custom event that Split.js will trigger
      window.addEventListener('split-resize', handleSplitResize);
    }

    return () => {
      window.removeEventListener('split-resize', handleSplitResize);
    };
  }, [chartType]);

  useEffect(() => {
    // Pass symbol change up to parent if onSymbolChanged handler is provided
    const handleSymbolChange = (e: CustomEvent) => {
      if (e.detail && e.detail.symbol && onSymbolChanged) {
        console.log(
          `TradingView component: Received symbol change event for ${e.detail.symbol}`
        );
        onSymbolChanged(e.detail.symbol);
      }
    };

    // Register event listeners for symbol changes
    window.addEventListener(
      'tradingview-symbol-change',
      handleSymbolChange as any
    );

    document.addEventListener(
      'watch-symbol-selected',
      handleSymbolChange as any
    );

    return () => {
      window.removeEventListener(
        'tradingview-symbol-change',
        handleSymbolChange as any
      );
      document.removeEventListener(
        'watch-symbol-selected',
        handleSymbolChange as any
      );
    };
  }, [onSymbolChanged]);

  // Handle loading state
  useEffect(() => {
    // Failsafe timeout to ensure loading screen doesn't get stuck
    const timer = setTimeout(() => {
      setIsLoading(false);
      if (onLoadComplete) {
        onLoadComplete();
      }
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [onLoadComplete]);

  // Render Recharts if chartType is 'recharts', otherwise render TradingView
  if (chartType === 'recharts') {
    return (
      <div
        className="chart-container w-full h-full relative overflow-hidden"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '300px',
          position: 'relative',
          overflow: 'hidden',
          contain: 'paint',
          pointerEvents: 'auto',
          display: 'flex',
        }}
      >
        <div
          className="w-full h-full bg-slate-900 rounded-xl border border-slate-800/30 overflow-hidden shadow-lg shadow-black/20 relative"
          style={{
            pointerEvents: 'auto',
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DashboardRecharts selectedSymbol={selectedSymbol} />
        </div>
      </div>
    );
  }

  // Original TradingView rendering code
  return (
    <div
      ref={containerRef}
      className="chart-container w-full h-full relative overflow-hidden"
      style={{
        width: '100%',
        height: '100%',
        minHeight: '300px',
        position: 'relative',
        overflow: 'hidden',
        contain: 'paint',
        pointerEvents: 'auto',
        display: 'flex',
      }}
    >
      {/* Inner container - ensure it also grows to full size */}
      <div
        className="w-full h-full bg-slate-900 rounded-xl border border-slate-800/30 overflow-hidden shadow-lg shadow-black/20 relative"
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TradingDashboard
          isTvLibraryReady={isTvLibraryReady}
          onLoadComplete={() => {
            setIsLoading(false);
            if (onLoadComplete) onLoadComplete();
            // Notify about container size after dashboard loads
            setTimeout(notifyChartContainerResize, 300);
          }}
        />
      </div>
    </div>
  );
}
